// ===== AUDIO GUEST BOOK — APPLICATION =====
//
// Drives three views: the intro cover, the museum-wall grid of polaroid
// "voice cards", and a two-column listening lightbox (real guest photo left;
// message + real audio player right — an <audio> element plays the guest's
// recording and a live Web Audio analyser drives the waveform).

import { fetchSubmissions } from './data.js';
import { formatTime, clamp, escapeHtml, showError } from './utils.js';

// Couple's-voice intro assets (bundled & fingerprinted by Vite).
import couplePhotoUrl from './assets/wedding-potraits.png';
import coupleAudioUrl from './assets/voice4-effect.wav';

export class GuestBook {
    constructor(weddingId) {
        this.weddingId = weddingId;
        this.submissions = [];
        this.currentIndex = 0;

        // Cover + wall
        this.coverEl = document.getElementById('cover');
        this.coverBtn = document.getElementById('cover-open');
        this.coverHint = document.getElementById('cover-hint');
        this.wallEl = document.getElementById('wall');
        this.gridEl = document.getElementById('gallery-grid');

        // Loader + couple's-voice intro
        this.loaderEl = document.getElementById('loader');
        this.introEl = document.getElementById('intro');
        this.introPhotoEl = document.getElementById('intro-photo');
        this.introPlayBtn = document.getElementById('intro-play');
        this.introWaveEl = document.getElementById('intro-wave');
        this.introWaveCtx = this.introWaveEl ? this.introWaveEl.getContext('2d') : null;
        this.introCurrentEl = document.getElementById('intro-current');
        this.introDurationEl = document.getElementById('intro-duration');
        this.introContinueBtn = document.getElementById('intro-continue');

        // Web Audio analyser state for the live waveform
        this.introAudio = null;
        this.audioCtx = null;
        this.analyser = null;
        this.timeData = null;
        this.waveBars = 64;            // number of bars in the waveform
        this.waveHist = new Array(this.waveBars).fill(0); // scrolling loudness
        this.introRaf = null;
        this.introRevealed = false;
        this.countLine = document.getElementById('count-line');

        // Lightbox
        this.modalEl = document.getElementById('player-modal');
        this.stageEl = document.querySelector('.lightbox__stage');
        this.autoplayBtn = document.getElementById('lb-autoplay');
        this.fsBtn = document.getElementById('lb-fullscreen');
        this.closeBtn = document.getElementById('lb-close');
        this.prevBtn = document.getElementById('lb-prev');
        this.nextBtn = document.getElementById('lb-next');
        this.photoEl = document.getElementById('lb-photo');
        this.polaroidNameEl = document.getElementById('lb-polaroid-name');
        this.metaEl = document.getElementById('lb-meta');
        this.nameEl = document.getElementById('lb-name');
        this.messageEl = document.getElementById('lb-message');
        this.counterEl = document.getElementById('lb-counter');
        this.thumbsEl = document.getElementById('lb-thumbs');
        this.thumbEls = []; // per-submission thumbnail buttons

        // Player
        this.playBtn = document.getElementById('lb-play');
        this.waveEl = document.getElementById('lb-wave');
        this.waveCtx = this.waveEl ? this.waveEl.getContext('2d') : null;
        this.currentEl = document.getElementById('lb-current');
        this.durationEl = document.getElementById('lb-duration');

        // Real audio playback state (driven by an <audio> element per guest)
        this.isPlaying = false;
        this.elapsed = 0;          // ms — mirrors audio.currentTime
        this.duration = 0;         // ms — mirrors audio.duration
        this.rafId = null;
        this.lbAudio = null;       // current guest's HTMLAudioElement
        this.waveform = [];        // stable static shape per guest
        this.waveCssW = 0;
        this.waveCssH = 0;
        this.autoSlide = false;    // true while auto-advancing through submissions

        this.init();
    }

    async init() {
        // Lock scroll behind the cover from the very start.
        document.body.classList.add('is-locked');

        try {
            this.submissions = await fetchSubmissions(this.weddingId);
        } catch (err) {
            console.error('Failed to load submissions:', err);
            showError('Failed to load the guest book. Please refresh.');
            this.submissions = [];
        }

        this.introPhotoEl.src = couplePhotoUrl;

        this.renderCounts();
        this.renderGrid();
        this.renderThumbs();
        this.attachCover();
        this.attachIntro();
        this.attachLightbox();

    }

    // ---- A stable hue per guest, for the duotone photo + watermark ----
    hueFor(submission, index) {
        if (Number.isFinite(submission.hue)) return submission.hue;
        // Spread hues evenly around the warm/full wheel, offset by id.
        const base = (submission.id ?? index) * 47;
        return (base % 360 + 360) % 360;
    }

    initialOf(name) {
        return (name || '?').trim().charAt(0).toUpperCase();
    }

    // ---- Counts shown on cover + masthead ----
    renderCounts() {
        const n = this.submissions.length;
        if (this.coverHint) {
            this.coverHint.textContent = `${n} ${n === 1 ? 'voice' : 'voices'} inside · tap to enter`;
        }
        if (this.countLine) {
            this.countLine.textContent = `${n} ${n === 1 ? 'Voice' : 'Voices'} Left For Us`;
        }
    }

    // ============================================================
    //  GRID
    // ============================================================
    renderGrid() {
        this.gridEl.innerHTML = '';

        if (this.submissions.length === 0) {
            this.gridEl.innerHTML =
                '<p style="grid-column:1/-1;text-align:center;color:var(--muted);font-family:var(--font-accent);font-style:italic;font-size:1.3rem">No voices yet. Check back soon.</p>';
            return;
        }

        this.submissions.forEach((s, index) => {
            const hue = this.hueFor(s, index);
            const initial = this.initialOf(s.guestName);

            const card = document.createElement('button');
            card.className = 'card';
            card.type = 'button';
            card.setAttribute('role', 'listitem');
            card.setAttribute('aria-label', `Listen to ${s.guestName}'s message`);
            card.style.setProperty('--hue', hue);
            card.style.setProperty('--delay', `${index * 80}ms`);

            card.innerHTML = `
                <div class="card__photo">
                    <span class="card__watermark" aria-hidden="true">${escapeHtml(initial)}</span>
                    <span class="card__sheen" aria-hidden="true"></span>
                </div>
                <div class="card__name">${escapeHtml(s.guestName)}</div>
                <div class="card__meta">
                    <span>${escapeHtml(s.timestamp || '')}</span>
                    <span class="card__dot" aria-hidden="true">·</span>
                    <span>&#9834; ${formatTime(s.duration)}</span>
                </div>
            `;

            // Show the guest's real photo when present (set via JS to avoid
            // quoting issues in inline style attributes). The duotone gradient
            // + watermark from CSS remain the fallback when there's no photo.
            if (s.photoUrl) {
                const photoEl = card.querySelector('.card__photo');
                photoEl.style.backgroundImage = `url("${encodeURI(s.photoUrl)}")`;
                photoEl.style.backgroundSize = 'cover';
                photoEl.style.backgroundPosition = 'center';
                photoEl.classList.add('has-photo');
                // Hide the initial watermark when a real photo is shown.
                photoEl.querySelector('.card__watermark')?.remove();
            }

            card.addEventListener('click', () => this.openLightbox(index));
            this.gridEl.appendChild(card);
        });
    }

    // ---- Filmstrip of mini thumbnails inside the lightbox ----
    renderThumbs() {
        if (!this.thumbsEl) return;
        this.thumbsEl.innerHTML = '';
        this.thumbEls = [];

        this.submissions.forEach((s, index) => {
            const thumb = document.createElement('button');
            thumb.className = 'lightbox__thumb';
            thumb.type = 'button';
            thumb.setAttribute('role', 'tab');
            thumb.setAttribute('aria-label', `${s.guestName || 'Guest'} (${index + 1} of ${this.submissions.length})`);

            if (s.photoUrl) {
                thumb.style.backgroundImage = `url("${encodeURI(s.photoUrl)}")`;
            } else {
                // No photo: a duotone tile with the guest's hue, like the cards.
                const hue = this.hueFor(s, index);
                thumb.style.background =
                    `linear-gradient(150deg, oklch(0.55 0.07 ${hue}), oklch(0.30 0.045 ${hue + 24}))`;
            }

            // Clicking a thumb jumps to that guest (and, like manual nav,
            // stops the slideshow). Keep the current play state.
            thumb.addEventListener('click', () => {
                if (index === this.currentIndex) return;
                this.setAutoSlide(false);
                this.openLightbox(index);
            });

            this.thumbsEl.appendChild(thumb);
            this.thumbEls.push(thumb);
        });
    }

    // Highlight the active thumbnail (colour) and scroll it into view.
    setActiveThumb(index) {
        this.thumbEls.forEach((el, i) => {
            const active = i === index;
            el.classList.toggle('is-active', active);
            el.setAttribute('aria-selected', String(active));
            if (active) {
                el.scrollIntoView({ block: 'nearest', inline: 'center' });
            }
        });
    }

    // ============================================================
    //  COVER
    // ============================================================
    attachCover() {
        const dismiss = () => this.dismissCover();

        this.coverBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            dismiss();
        });

        // Clicking anywhere on the cover dismisses it too.
        this.coverEl.addEventListener('click', dismiss);

        // Enter / Esc while the cover is up.
        this._coverKey = (e) => {
            if (this.coverEl.classList.contains('is-dismissed')) return;
            if (e.key === 'Enter' || e.key === 'Escape' || e.key === ' ') {
                e.preventDefault();
                dismiss();
            }
        };
        document.addEventListener('keydown', this._coverKey);
    }

    dismissCover() {
        if (this.coverEl.classList.contains('is-dismissed')) return;

        this.coverEl.classList.add('is-dismissed');
        document.removeEventListener('keydown', this._coverKey);

        // Enter fullscreen on the whole page — must be called within the user gesture.
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.().catch(() => {});
        }

        // Set up the audio inside this user gesture so playback is allowed.
        this.prepareIntroAudio();

        // Cover → minimalist loader → couple's-voice intro.
        this.loaderEl.classList.add('is-active');
        window.setTimeout(() => {
            this.loaderEl.classList.remove('is-active');
            this.openIntro();
        }, 1400);
        // Scroll stays locked through the loader + intro.
    }

    // ============================================================
    //  COUPLE'S VOICE INTRO
    // ============================================================
    attachIntro() {
        this.introContinueBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.revealWall();
        });
        this.introPlayBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleIntroAudio();
        });

        // Keep the canvas crisp on resize while the intro is up.
        window.addEventListener('resize', () => {
            if (this.introEl.classList.contains('is-active')) this.setupIntroCanvas();
        });
    }

    prepareIntroAudio() {
        if (this.introAudio) return;

        this.introAudio = new Audio(coupleAudioUrl);
        this.introAudio.preload = 'auto';

        // Live waveform via Web Audio analyser.
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            this.audioCtx = new Ctx();
            const source = this.audioCtx.createMediaElementSource(this.introAudio);
            this.analyser = this.audioCtx.createAnalyser();
            this.analyser.fftSize = 1024;
            this.analyser.smoothingTimeConstant = 0.75;
            // Time-domain samples — we read overall loudness (RMS), not per-band.
            this.timeData = new Uint8Array(this.analyser.fftSize);
            source.connect(this.analyser);
            this.analyser.connect(this.audioCtx.destination);
        } catch (err) {
            console.warn('Web Audio unavailable; waveform will use progress only.', err);
        }

        this.introAudio.addEventListener('loadedmetadata', () => {
            this.introDurationEl.textContent = formatTime(this.introAudio.duration * 1000);
        });
        this.introAudio.addEventListener('timeupdate', () => {
            this.introCurrentEl.textContent = formatTime(this.introAudio.currentTime * 1000);
        });
        this.introAudio.addEventListener('play', () => this.introPlayBtn.classList.remove('is-paused'));
        this.introAudio.addEventListener('pause', () => this.introPlayBtn.classList.add('is-paused'));
        // When the couple's message finishes, move on to the gallery.
        this.introAudio.addEventListener('ended', () => this.revealWall());
    }

    openIntro() {
        this.introEl.classList.add('is-active');
        this.introEl.setAttribute('aria-hidden', 'false');

        this.waveHist.fill(0);          // start from a flat line
        this.setupIntroCanvas();
        this.drawIntroWave();           // paint an idle baseline immediately

        // Auto-play (allowed: we're still inside the open-button gesture chain).
        if (this.audioCtx && this.audioCtx.state === 'suspended') {
            this.audioCtx.resume().catch(() => {});
        }
        const p = this.introAudio.play();
        if (p && p.catch) {
            p.catch(() => {
                // Autoplay blocked — leave it paused; the button starts it.
                this.introPlayBtn.classList.add('is-paused');
            });
        }
        this.introLoop();
    }

    toggleIntroAudio() {
        if (!this.introAudio) return;
        if (this.introAudio.paused) {
            if (this.audioCtx?.state === 'suspended') this.audioCtx.resume().catch(() => {});
            this.introAudio.play().catch(() => {});
            this.introLoop();
        } else {
            this.introAudio.pause();
        }
    }

    setupIntroCanvas() {
        if (!this.introWaveCtx) return;
        const dpr = window.devicePixelRatio || 1;
        const cssW = this.introWaveEl.clientWidth || 900;
        const cssH = this.introWaveEl.clientHeight || 80;
        this.introWaveCssW = cssW;
        this.introWaveCssH = cssH;
        this.introWaveEl.width = Math.round(cssW * dpr);
        this.introWaveEl.height = Math.round(cssH * dpr);
        this.introWaveCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    introLoop() {
        if (this.introRaf) cancelAnimationFrame(this.introRaf);
        const step = () => {
            this.drawIntroWave();
            if (this.introAudio && !this.introAudio.paused) {
                this.introRaf = requestAnimationFrame(step);
            }
        };
        this.introRaf = requestAnimationFrame(step);
    }

    // Overall loudness (0..1) of the current audio frame, via time-domain RMS.
    currentLoudness() {
        if (!this.analyser || !this.introAudio || this.introAudio.paused) return 0;
        this.analyser.getByteTimeDomainData(this.timeData);
        let sum = 0;
        for (let i = 0; i < this.timeData.length; i++) {
            const v = (this.timeData[i] - 128) / 128; // -1..1 around silence (128)
            sum += v * v;
        }
        const rms = Math.sqrt(sum / this.timeData.length);
        // Speech RMS is small; scale up and clamp for a lively but bounded bar.
        return clamp(rms * 3.2, 0, 1);
    }

    drawIntroWave() {
        const ctx = this.introWaveCtx;
        if (!ctx) return;

        const w = this.introWaveCssW || this.introWaveEl.clientWidth;
        const h = this.introWaveCssH || this.introWaveEl.clientHeight;
        const bars = this.waveBars;
        const slot = w / bars;
        const barW = Math.max(2, slot * 0.5);
        const radius = barW / 2;
        const centerY = h / 2;
        const round = typeof ctx.roundRect === 'function';

        const playing = this.introAudio && !this.introAudio.paused;

        // Advance the scrolling history: newest loudness enters on the right,
        // everything shifts left. This animates the bars evenly across the
        // whole width instead of clustering at one end.
        if (playing) {
            this.waveHist.shift();
            this.waveHist.push(this.currentLoudness());
        }

        // Playback progress for the champagne "played" fill.
        const dur = this.introAudio?.duration || 0;
        const progress = dur ? clamp(this.introAudio.currentTime / dur, 0, 1) : 0;

        ctx.clearRect(0, 0, w, h);

        for (let i = 0; i < bars; i++) {
            let amp;
            if (playing) {
                amp = this.waveHist[i];
            } else {
                // Idle / paused: a calm, gently varied static shape.
                amp = 0.12 + 0.07 * Math.abs(Math.sin(i * 0.5));
            }

            const barH = Math.max(3, amp * h * 0.92);
            const x = i * slot + (slot - barW) / 2;
            const y = centerY - barH / 2;

            const played = (i + 0.5) / bars <= progress;
            ctx.fillStyle = played ? 'oklch(0.83 0.045 88)' : 'rgba(236, 232, 224, 0.22)';

            ctx.beginPath();
            if (round) ctx.roundRect(x, y, barW, barH, radius);
            else ctx.rect(x, y, barW, barH);
            ctx.fill();
        }
    }

    revealWall() {
        if (this.introRevealed) return;
        this.introRevealed = true;

        // Stop the intro audio + animation.
        if (this.introRaf) cancelAnimationFrame(this.introRaf);
        this.introRaf = null;
        if (this.introAudio) {
            this.introAudio.pause();
            this.introAudio.currentTime = 0;
        }

        this.introEl.classList.remove('is-active');
        this.introEl.setAttribute('aria-hidden', 'true');
        this.loaderEl.classList.remove('is-active');

        document.body.classList.remove('is-locked');
        this.wallEl.classList.add('is-revealed');

        // Auto-open the lightbox at the first guest and start playing.
        if (this.submissions.length > 0) {
            this.setAutoSlide(true);
            this.openLightbox(0);
            this.play();
        }
    }

    // ============================================================
    //  LIGHTBOX
    // ============================================================
    attachLightbox() {
        this.closeBtn.addEventListener('click', () => this.closeLightbox());
        this.prevBtn.addEventListener('click', () => this.navigate(-1));
        this.nextBtn.addEventListener('click', () => this.navigate(1));
        this.autoplayBtn.addEventListener('click', () => this.toggleAutoSlide());
        this.fsBtn.addEventListener('click', () => this.toggleFullscreen());

        // Keep our state in sync however the browser leaves full screen
        // (our button, the F key, Esc, or the browser chrome).
        document.addEventListener('fullscreenchange', () => this.syncFullscreen());

        // Click on the scrim (but not the stage) closes.
        this.modalEl.addEventListener('click', (e) => {
            if (e.target === this.modalEl) this.closeLightbox();
        });

        // Player controls — the waveform doubles as the seek surface.
        this.playBtn.addEventListener('click', () => this.togglePlay());
        this.waveEl.addEventListener('click', (e) => this.seekFromEvent(e));
        this.waveEl.addEventListener('keydown', (e) => this.barKeydown(e));

        // Keep the waveform crisp when the viewport resizes (only when open).
        window.addEventListener('resize', () => {
            if (this.modalEl.classList.contains('is-open')) {
                this.setupWaveCanvas();
                this.drawWaveform();
            }
        });

        // Global keys while the lightbox is open.
        document.addEventListener('keydown', (e) => this.lightboxKeydown(e));
    }

    // Single source of truth for the slideshow flag + its button state.
    setAutoSlide(on) {
        this.autoSlide = on;
        this.autoplayBtn.classList.toggle('is-active', on);
        this.autoplayBtn.setAttribute('aria-pressed', String(on));
        this.autoplayBtn.setAttribute(
            'aria-label',
            on ? 'Stop slideshow' : 'Start slideshow'
        );
    }

    // Toggle button: when turning on, (re)start playback from the current guest.
    toggleAutoSlide() {
        if (this.autoSlide) {
            this.setAutoSlide(false);
        } else {
            this.setAutoSlide(true);
            this.play();
        }
    }

    openLightbox(index) {
        this.currentIndex = index;

        // Reveal first so the canvas is laid out (non-zero width) before we
        // size and draw the waveform inside loadSubmission().
        this.modalEl.classList.add('is-open');
        this.modalEl.setAttribute('aria-hidden', 'false');
        document.body.classList.add('is-locked');

        this.loadSubmission(this.submissions[index]);

        this.closeBtn.focus();
    }

    closeLightbox() {
        this.setAutoSlide(false);
        this.stop();
        // Leave full screen along with the lightbox.
        if (document.fullscreenElement) {
            document.exitFullscreen?.().catch(() => {});
        }
        this.modalEl.classList.remove('is-open');
        this.modalEl.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-locked');
        // Clear any in-flight slideshow transition state.
        this.stageEl?.classList.remove('is-leaving', 'is-entering');
    }

    // ============================================================
    //  FULL SCREEN
    // ============================================================
    toggleFullscreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen?.().catch(() => {});
        } else {
            // Request on the modal so the listening view fills the screen.
            this.modalEl.requestFullscreen?.().catch((err) => {
                console.warn('Fullscreen request failed:', err);
            });
        }
        // The fullscreenchange handler does the visible state update.
    }

    syncFullscreen() {
        const on = document.fullscreenElement === this.modalEl;
        this.modalEl.classList.toggle('is-fullscreen', on);
        this.fsBtn.setAttribute('aria-label', on ? 'Exit full screen' : 'Enter full screen');

        // The canvas changed size; re-measure and repaint on the next frame
        // (after layout settles).
        requestAnimationFrame(() => {
            if (this.modalEl.classList.contains('is-open')) {
                this.setupWaveCanvas();
                this.drawWaveform();
            }
        });
    }

    loadSubmission(s) {
        this.stop();

        const hue = this.hueFor(s, this.currentIndex);
        const initial = this.initialOf(s.guestName);

        if (s.photoUrl) {
            // Real guest photo fills the enlarged polaroid.
            this.photoEl.style.background = `center / cover no-repeat url("${encodeURI(s.photoUrl)}")`;
            this.photoEl.style.display = '';
            this.photoEl.style.placeItems = '';
            this.photoEl.innerHTML = '';
        } else {
            // Fallback: full-colour duotone with the guest's initial (matches the card).
            this.photoEl.style.background = `
                radial-gradient(120% 80% at 30% 20%, rgba(255,255,255,0.16), transparent 55%),
                linear-gradient(150deg,
                    oklch(0.58 0.085 ${hue}) 0%,
                    oklch(0.36 0.06 ${hue + 18}) 60%,
                    oklch(0.24 0.035 ${hue + 30}) 100%)`;
            this.photoEl.style.display = 'grid';
            this.photoEl.style.placeItems = 'center';
            this.photoEl.innerHTML =
                `<span style="font-family:var(--font-display);font-weight:500;font-size:9rem;color:rgba(255,255,255,0.12)">${escapeHtml(initial)}</span>`;
        }

        // Polaroid caption (under the photo, left column).
        this.polaroidNameEl.textContent = s.guestName;

        // Right panel: meta · name · message.
        this.metaEl.textContent = `Voice message · ${s.timestamp || ''}`;
        this.nameEl.textContent = s.guestName;
        this.messageEl.textContent = s.caption || 'Left a voice message for the couple.';

        this.counterEl.textContent =
            `${this.currentIndex + 1} / ${this.submissions.length}`;
        this.setActiveThumb(this.currentIndex);

        // Reset the player and load this guest's real recording.
        this.elapsed = 0;
        this.duration = 0;
        this.waveform = this.buildWaveform(s.id ?? this.currentIndex); // fallback shape
        this.setupWaveCanvas();
        this.loadAudio(s.audioUrl);
        this.durationEl.textContent = formatTime(this.duration);
        this.renderProgress();

        this.prevBtn.disabled = this.currentIndex === 0;
        this.nextBtn.disabled = this.currentIndex === this.submissions.length - 1;
    }

    navigate(dir) {
        this.setAutoSlide(false);
        const next = this.currentIndex + dir;
        if (next < 0 || next >= this.submissions.length) return;
        this.openLightbox(next);
    }

    // ============================================================
    //  AUDIO PLAYER (real recordings)
    // ============================================================

    // Tear down the previous guest's audio before loading the next.
    teardownAudio() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = null;
        if (this.lbAudio) {
            this.lbAudio.pause();
            this.lbAudio.removeAttribute('src');
            this.lbAudio.load();
            this.lbAudio = null;
        }
    }

    // Create the <audio> element for this guest and wire up its events.
    loadAudio(url) {
        this.teardownAudio();

        if (!url) {
            // No recording — leave the player idle with the static waveform.
            this.duration = 0;
            return;
        }

        // No crossOrigin: the audio is served from a different domain (vgb2),
        // and requiring CORS would risk blocking playback. Playback itself
        // doesn't need it. The waveform uses the guest's stable static shape
        // with a champagne progress fill.
        const audio = new Audio(url);
        audio.preload = 'auto';
        this.lbAudio = audio;

        audio.addEventListener('loadedmetadata', () => {
            this.duration = (audio.duration || 0) * 1000;
            this.durationEl.textContent = formatTime(this.duration);
            this.renderProgress();
        });
        audio.addEventListener('ended', () => this.onEnded());
        audio.addEventListener('error', () => {
            console.warn('Audio failed to load:', url);
        });
    }

    togglePlay() {
        this.isPlaying ? this.pause() : this.play();
    }

    play() {
        if (!this.lbAudio || this.isPlaying) return;
        // Restart if we're at the end.
        if (this.lbAudio.ended || this.lbAudio.currentTime >= (this.lbAudio.duration || 0)) {
            this.lbAudio.currentTime = 0;
        }

        this.isPlaying = true;
        this.playBtn.parentElement.classList.add('is-playing');
        this.playBtn.setAttribute('aria-label', 'Pause');
        this.lbAudio.play().catch(() => {
            // Autoplay blocked — revert to paused; the button starts it on tap.
            this.isPlaying = false;
            this.playBtn.parentElement.classList.remove('is-playing');
            this.playBtn.setAttribute('aria-label', 'Play');
        });
        this.rafId = requestAnimationFrame(() => this.tick());
    }

    pause() {
        this.isPlaying = false;
        this.playBtn.parentElement.classList.remove('is-playing');
        this.playBtn.setAttribute('aria-label', 'Play');
        if (this.lbAudio) this.lbAudio.pause();
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = null;
    }

    stop() {
        this.pause();
        if (this.lbAudio) this.lbAudio.currentTime = 0;
        this.elapsed = 0;
        this.renderProgress();
    }

    // Reached the end of a recording.
    onEnded() {
        this.elapsed = this.duration;
        this.renderProgress();
        this.pause();

        if (this.autoSlide) {
            const next = this.currentIndex + 1;
            if (next < this.submissions.length) {
                window.setTimeout(() => {
                    this.crossfadeTo(next, () => this.play());
                }, 700);
            } else {
                this.setAutoSlide(false); // reached the last guest
            }
        }
    }

    // Slideshow transition: fade the stage out, swap to the next guest, then
    // fade the polaroid and the text/player back in with a gentle stagger.
    crossfadeTo(index, after) {
        if (index === this.currentIndex && this.modalEl.classList.contains('is-open')) {
            if (after) after();
            return;
        }

        const stage = this.stageEl;
        if (!stage) {
            this.openLightbox(index);
            if (after) after();
            return;
        }

        // 1) Fade the current slide out.
        stage.classList.add('is-leaving');

        window.setTimeout(() => {
            // 2) Swap content while invisible.
            this.openLightbox(index);

            // 3) Replay the staggered fade-in. Removing then re-adding the
            //    class on the next frame restarts the CSS animations.
            stage.classList.remove('is-leaving');
            stage.classList.remove('is-entering');
            // Force reflow so the animation restarts even on rapid advances.
            void stage.offsetWidth;
            stage.classList.add('is-entering');

            if (after) after();
        }, 360); // matches the fade-out duration in CSS
    }

    tick() {
        if (!this.isPlaying || !this.lbAudio) return;
        this.elapsed = this.lbAudio.currentTime * 1000;
        this.renderProgress();
        this.rafId = requestAnimationFrame(() => this.tick());
    }

    renderProgress() {
        const pct = this.duration ? clamp(this.elapsed / this.duration, 0, 1) : 0;
        this.currentEl.textContent = formatTime(this.elapsed);
        this.waveEl.setAttribute('aria-valuenow', Math.round(pct * 100));
        this.drawWaveform();
    }

    seekFromEvent(e) {
        const rect = this.waveEl.getBoundingClientRect();
        const pct = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        this.seekTo(pct);
    }

    // ---- Waveform ----

    // Deterministic amplitudes (0..1) so each guest has a stable shape.
    buildWaveform(seed, bars = 56) {
        const out = [];
        const s = (seed || 0) + 1;
        for (let i = 0; i < bars; i++) {
            const a = Math.sin(i * 0.45 + s) * Math.cos(i * 0.27 + s * 1.7);
            const b = Math.sin(i * 0.13 + s * 0.5);
            out.push(clamp(Math.abs(a) * 0.6 + Math.abs(b) * 0.25 + 0.18, 0.08, 1));
        }
        return out;
    }

    // Match the backing store to the rendered size × DPR (canvas must be visible).
    setupWaveCanvas() {
        if (!this.waveCtx) return;
        const dpr = window.devicePixelRatio || 1;
        const cssW = this.waveEl.clientWidth || 600;
        const cssH = this.waveEl.clientHeight || 56;
        this.waveCssW = cssW;
        this.waveCssH = cssH;
        this.waveEl.width = Math.round(cssW * dpr);
        this.waveEl.height = Math.round(cssH * dpr);
        this.waveCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    drawWaveform() {
        if (!this.waveCtx || this.waveform.length === 0) return;

        const w = this.waveCssW || this.waveEl.clientWidth;
        const h = this.waveCssH || this.waveEl.clientHeight;
        const n = this.waveform.length;
        const progress = this.duration ? clamp(this.elapsed / this.duration, 0, 1) : 0;

        const slot = w / n;
        const barW = Math.max(2, slot * 0.55);
        const radius = barW / 2;
        const minH = 3;
        const centerY = h / 2;
        const round = typeof this.waveCtx.roundRect === 'function';

        this.waveCtx.clearRect(0, 0, w, h);

        for (let i = 0; i < n; i++) {
            const barH = Math.max(minH, this.waveform[i] * h * 0.92);
            const x = i * slot + (slot - barW) / 2;
            const y = centerY - barH / 2;

            // Played bars are champagne; the rest are faint.
            const played = (i + 0.5) / n <= progress;
            this.waveCtx.fillStyle = played
                ? 'oklch(0.83 0.045 88)'
                : 'rgba(236, 232, 224, 0.20)';

            this.waveCtx.beginPath();
            if (round) this.waveCtx.roundRect(x, y, barW, barH, radius);
            else this.waveCtx.rect(x, y, barW, barH);
            this.waveCtx.fill();
        }
    }

    seekTo(pct) {
        this.elapsed = pct * this.duration;
        if (this.lbAudio && this.duration > 0) {
            this.lbAudio.currentTime = (this.elapsed / 1000);
        }
        this.renderProgress();
    }

    barKeydown(e) {
        const step = 0.05;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.seekTo(clamp(this.elapsed / this.duration + step, 0, 1));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.seekTo(clamp(this.elapsed / this.duration - step, 0, 1));
        } else if (e.key === 'Home') {
            e.preventDefault();
            this.seekTo(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            this.seekTo(1);
        }
    }

    lightboxKeydown(e) {
        if (!this.modalEl.classList.contains('is-open')) return;

        switch (e.key) {
            case 'Escape':
                // In full screen, the browser handles Esc itself (it exits
                // full screen). Don't also close the lightbox on that press.
                if (document.fullscreenElement) return;
                this.closeLightbox();
                break;
            case 'ArrowLeft':
                if (e.target === this.waveEl) return; // let the seek surface handle it
                this.navigate(-1);
                break;
            case 'ArrowRight':
                if (e.target === this.waveEl) return;
                this.navigate(1);
                break;
            case ' ':
                if (e.target === this.waveEl || e.target === this.playBtn) return;
                e.preventDefault();
                this.togglePlay();
                break;
            case 'f':
            case 'F':
                if (e.target === this.waveEl) return;
                e.preventDefault();
                this.toggleFullscreen();
                break;
        }
    }
}
